import { useState, useMemo, useEffect } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import { useOrders } from '../../../contexts/OrdersContext';

interface StoredCustomer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  createdAt: string;
  avatar?: string;
}

const STORAGE_KEY = 'sipdash_users';

function getStoredCustomers(): StoredCustomer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<StoredCustomer & { password?: string }>;
    return parsed.map(({ password: _pw, ...safe }) => safe);
  } catch {
    return [];
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function isNewThisMonth(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function isNewThisWeek(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 3600000);
  return d >= weekAgo;
}

type SortOption = 'newest' | 'oldest' | 'name_asc' | 'name_desc' | 'most_orders';

interface CustomerDetailModalProps {
  customer: StoredCustomer;
  orderCount: number;
  totalSpent: number;
  lastOrder?: string;
  orders: Array<{ id: string; status: string; total: number; createdAt: string }>;
  onClose: () => void;
}

function CustomerDetailModal({ customer, orderCount, totalSpent, lastOrder, orders, onClose }: CustomerDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
          <h2 className="text-base font-bold text-stone-900">Customer Details</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-700 transition cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Avatar + info */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl font-bold">{customer.name.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-stone-900 truncate">{customer.name}</p>
              <p className="text-sm text-stone-500 truncate">{customer.email}</p>
              {customer.phone && (
                <p className="text-xs text-stone-400 mt-0.5">{customer.phone}</p>
              )}
            </div>
            {isNewThisWeek(customer.createdAt) && (
              <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">New</span>
            )}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-stone-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-stone-900">{orderCount}</p>
              <p className="text-xs text-stone-500 mt-0.5">Orders</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-orange-600">€{totalSpent.toFixed(2)}</p>
              <p className="text-xs text-stone-500 mt-0.5">Total Spent</p>
            </div>
            <div className="bg-stone-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-stone-900">
                {orderCount > 0 ? `€${(totalSpent / orderCount).toFixed(2)}` : '—'}
              </p>
              <p className="text-xs text-stone-500 mt-0.5">Avg. Order</p>
            </div>
          </div>

          {/* Info fields */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-3 py-2.5 border-b border-stone-100">
              <span className="w-7 h-7 flex items-center justify-center text-stone-400">
                <i className="ri-mail-line text-base" />
              </span>
              <div>
                <p className="text-xs text-stone-400 leading-none mb-0.5">Email</p>
                <p className="text-sm font-medium text-stone-800">{customer.email}</p>
              </div>
            </div>
            {customer.phone && (
              <div className="flex items-center gap-3 py-2.5 border-b border-stone-100">
                <span className="w-7 h-7 flex items-center justify-center text-stone-400">
                  <i className="ri-phone-line text-base" />
                </span>
                <div>
                  <p className="text-xs text-stone-400 leading-none mb-0.5">Phone</p>
                  <p className="text-sm font-medium text-stone-800">{customer.phone}</p>
                </div>
              </div>
            )}
            {customer.address && (
              <div className="flex items-center gap-3 py-2.5 border-b border-stone-100">
                <span className="w-7 h-7 flex items-center justify-center text-stone-400">
                  <i className="ri-map-pin-line text-base" />
                </span>
                <div>
                  <p className="text-xs text-stone-400 leading-none mb-0.5">Address</p>
                  <p className="text-sm font-medium text-stone-800">{customer.address}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 py-2.5">
              <span className="w-7 h-7 flex items-center justify-center text-stone-400">
                <i className="ri-calendar-line text-base" />
              </span>
              <div>
                <p className="text-xs text-stone-400 leading-none mb-0.5">Registered</p>
                <p className="text-sm font-medium text-stone-800">{formatDate(customer.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Recent orders */}
          {orders.length > 0 && (
            <div>
              <p className="text-xs font-bold text-stone-500 uppercase tracking-wide mb-3">Recent Orders</p>
              <div className="space-y-2">
                {orders.slice(0, 5).map((order) => {
                  const statusColors: Record<string, string> = {
                    delivered: 'text-green-600 bg-green-50',
                    cancelled: 'text-red-500 bg-red-50',
                    pending: 'text-amber-600 bg-amber-50',
                    processing: 'text-blue-600 bg-blue-50',
                    on_way: 'text-orange-600 bg-orange-50',
                    payment_pending: 'text-stone-600 bg-stone-100',
                  };
                  const colorClass = statusColors[order.status] || 'text-stone-600 bg-stone-100';
                  return (
                    <div key={order.id} className="flex items-center justify-between px-3 py-2.5 bg-stone-50 rounded-xl">
                      <div className="flex items-center gap-2.5">
                        <span className="text-xs font-mono font-bold text-stone-600">{order.id}</span>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colorClass}`}>
                          {order.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-stone-800">€{order.total.toFixed(2)}</p>
                        <p className="text-xs text-stone-400">{timeAgo(order.createdAt)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {orders.length === 0 && (
            <div className="text-center py-4">
              <span className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-shopping-bag-line text-stone-400 text-lg" />
              </span>
              <p className="text-sm text-stone-400">No orders yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCustomersPage() {
  const { orders } = useOrders();
  const [customers, setCustomers] = useState<StoredCustomer[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [filterNew, setFilterNew] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<StoredCustomer | null>(null);

  useEffect(() => {
    setCustomers(getStoredCustomers());
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setCustomers(getStoredCustomers());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const customerStats = useMemo(() => {
    return customers.map((c) => {
      const cOrders = orders.filter((o) => o.userId === c.id);
      const totalSpent = cOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const lastOrder = cOrders.length > 0
        ? cOrders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0].createdAt
        : undefined;
      return { id: c.id, orderCount: cOrders.length, totalSpent, lastOrder, orders: cOrders };
    });
  }, [customers, orders]);

  const statsMap = useMemo(() => {
    const map: Record<string, (typeof customerStats)[number]> = {};
    customerStats.forEach((s) => { map[s.id] = s; });
    return map;
  }, [customerStats]);

  const filteredCustomers = useMemo(() => {
    let list = [...customers];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          (c.phone ?? '').includes(q)
      );
    }
    if (filterNew) list = list.filter((c) => isNewThisMonth(c.createdAt));

    list.sort((a, b) => {
      if (sort === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sort === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sort === 'name_asc') return a.name.localeCompare(b.name);
      if (sort === 'name_desc') return b.name.localeCompare(a.name);
      if (sort === 'most_orders') return (statsMap[b.id]?.orderCount ?? 0) - (statsMap[a.id]?.orderCount ?? 0);
      return 0;
    });
    return list;
  }, [customers, search, sort, filterNew, statsMap]);

  const totalCustomers = customers.length;
  const newThisMonth = customers.filter((c) => isNewThisMonth(c.createdAt)).length;
  const newThisWeek = customers.filter((c) => isNewThisWeek(c.createdAt)).length;
  const withOrders = customers.filter((c) => (statsMap[c.id]?.orderCount ?? 0) > 0).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Customers</h1>
            <p className="text-sm text-stone-500 mt-0.5">View and search all registered customers</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-stone-100 rounded-xl">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-3-line text-stone-500 text-sm" />
            </span>
            <span className="text-sm font-bold text-stone-700">{totalCustomers} total</span>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-3-line text-orange-500 text-base" />
              </span>
              <span className="text-xs text-stone-400">Total</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{totalCustomers}</p>
            <p className="text-xs text-stone-500 mt-1">Registered customers</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-add-line text-green-500 text-base" />
              </span>
              <span className="text-xs text-stone-400">This Month</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{newThisMonth}</p>
            <p className="text-xs text-stone-500 mt-1">New registrations</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-calendar-line text-amber-500 text-base" />
              </span>
              <span className="text-xs text-stone-400">This Week</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{newThisWeek}</p>
            <p className="text-xs text-stone-500 mt-1">New this week</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-shopping-bag-line text-orange-500 text-base" />
              </span>
              <span className="text-xs text-stone-400">Active</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{withOrders}</p>
            <p className="text-xs text-stone-500 mt-1">Placed at least 1 order</p>
          </div>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-stone-400 text-sm" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or phone..."
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-stone-200 rounded-xl bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-stone-400 hover:text-stone-600 transition cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>

          {/* New this month filter */}
          <button
            onClick={() => setFilterNew(!filterNew)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-xl border transition cursor-pointer whitespace-nowrap ${
              filterNew
                ? 'bg-green-500 text-white border-green-500'
                : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-add-line text-sm" />
            </span>
            New this month
          </button>

          {/* Sort */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
              className="appearance-none pl-3 pr-8 py-2.5 text-sm font-medium border border-stone-200 rounded-xl bg-white text-stone-700 outline-none focus:border-orange-400 cursor-pointer transition hover:bg-stone-50"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name_asc">Name A–Z</option>
              <option value="name_desc">Name Z–A</option>
              <option value="most_orders">Most orders</option>
            </select>
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center text-stone-400">
              <i className="ri-arrow-down-s-line text-sm" />
            </span>
          </div>
        </div>

        {/* Results count */}
        <p className="text-xs text-stone-400 -mt-2">
          Showing <strong className="text-stone-600">{filteredCustomers.length}</strong> of {totalCustomers} customers
          {filterNew && <span className="text-green-600 ml-1">&middot; filtered by new this month</span>}
        </p>

        {/* Customer list */}
        {totalCustomers === 0 ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-16 text-center">
            <span className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-3-line text-stone-400 text-3xl" />
            </span>
            <p className="text-base font-semibold text-stone-700 mb-1">No customers yet</p>
            <p className="text-sm text-stone-400">Customers will appear here after they register on the platform</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-12 text-center">
            <span className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <i className="ri-search-line text-stone-400 text-2xl" />
            </span>
            <p className="text-sm font-semibold text-stone-700 mb-1">No results found</p>
            <p className="text-xs text-stone-400">Try a different search term or remove filters</p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="text-left px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Contact</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Orders</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Total Spent</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Registered</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer, idx) => {
                  const stat = statsMap[customer.id];
                  const orderCount = stat?.orderCount ?? 0;
                  const totalSpent = stat?.totalSpent ?? 0;
                  const isNew = isNewThisWeek(customer.createdAt);

                  return (
                    <tr
                      key={customer.id}
                      className={`border-b border-stone-100 last:border-0 hover:bg-orange-50/40 transition cursor-pointer ${idx % 2 === 0 ? '' : 'bg-stone-50/30'}`}
                      onClick={() => setSelectedCustomer(customer)}
                    >
                      {/* Customer name + avatar */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-bold">{customer.name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-stone-900">{customer.name}</p>
                              {isNew && (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">NEW</span>
                              )}
                            </div>
                            <p className="text-xs text-stone-400 font-mono">{customer.id.slice(0, 12)}...</p>
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-stone-700">{customer.email}</p>
                        {customer.phone && (
                          <p className="text-xs text-stone-400 mt-0.5">{customer.phone}</p>
                        )}
                      </td>

                      {/* Orders */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                            orderCount === 0
                              ? 'bg-stone-100 text-stone-500'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            <i className="ri-shopping-bag-line text-xs" />
                            {orderCount}
                          </span>
                        </div>
                      </td>

                      {/* Total spent */}
                      <td className="px-5 py-3.5">
                        <p className={`text-sm font-bold ${totalSpent > 0 ? 'text-stone-900' : 'text-stone-400'}`}>
                          {totalSpent > 0 ? `€${totalSpent.toFixed(2)}` : '—'}
                        </p>
                      </td>

                      {/* Registered */}
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-stone-600">{timeAgo(customer.createdAt)}</p>
                        <p className="text-xs text-stone-400 mt-0.5">
                          {new Date(customer.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </td>

                      {/* Action */}
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedCustomer(customer); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 bg-stone-100 hover:bg-orange-100 hover:text-orange-700 rounded-lg transition cursor-pointer whitespace-nowrap ml-auto"
                        >
                          <i className="ri-eye-line text-xs" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedCustomer && (() => {
        const stat = statsMap[selectedCustomer.id];
        return (
          <CustomerDetailModal
            customer={selectedCustomer}
            orderCount={stat?.orderCount ?? 0}
            totalSpent={stat?.totalSpent ?? 0}
            lastOrder={stat?.lastOrder}
            orders={(stat?.orders ?? []).map((o) => ({
              id: o.id,
              status: o.status,
              total: o.total ?? 0,
              createdAt: o.createdAt,
            }))}
            onClose={() => setSelectedCustomer(null)}
          />
        );
      })()}
    </AdminLayout>
  );
}
